import { useTranslation } from 'react-i18next';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Icon,
  Text,
  HStack,
  Image
} from '@chakra-ui/react';
import { MdLanguage } from 'react-icons/md';

// SVG flag components for reliable cross-platform display
const FrenchFlag = () => (
  <svg width="20" height="15" viewBox="0 0 3 2" style={{ borderRadius: '2px', boxShadow: '0 0 1px rgba(0,0,0,0.3)' }}>
    <rect width="1" height="2" fill="#002395" />
    <rect x="1" width="1" height="2" fill="#FFFFFF" />
    <rect x="2" width="1" height="2" fill="#ED2939" />
  </svg>
);

const BritishFlag = () => (
  <svg width="20" height="15" viewBox="0 0 60 30" style={{ borderRadius: '2px', boxShadow: '0 0 1px rgba(0,0,0,0.3)' }}>
    <clipPath id="s"><path d="M0,0 v30 h60 v-30 z"/></clipPath>
    <clipPath id="t"><path d="M30,15 h30 v15 z v-15 h-30 z h-30 v-15 z v15 h30 z"/></clipPath>
    <g clipPath="url(#s)">
      <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
    </g>
  </svg>
);

interface LanguageSwitcherProps {
  variant?: 'button' | 'menu';
  showIcon?: boolean;
}

export const LanguageSwitcher = ({ 
  variant = 'menu',
  showIcon = true 
}: LanguageSwitcherProps) => {
  const { i18n } = useTranslation();

  const languages = [
    { code: 'fr', name: 'Français', Flag: FrenchFlag },
    { code: 'en', name: 'English', Flag: BritishFlag }
  ];

  // Get the base language code (e.g., 'en' from 'en-US')
  const baseLanguage = i18n.language.split('-')[0];
  const currentLanguage = languages.find(lang => lang.code === baseLanguage) || languages[0];

  const changeLanguage = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    // Save to Firestore user preferences (implement this in Settings component)
  };

  if (variant === 'button') {
    return (
      <HStack spacing={2}>
        {languages.map(lang => (
          <Button
            key={lang.code}
            size="sm"
            variant={baseLanguage === lang.code ? 'solid' : 'ghost'}
            colorScheme={baseLanguage === lang.code ? 'brand' : 'gray'}
            onClick={() => changeLanguage(lang.code)}
            leftIcon={<lang.Flag />}
          >
            {lang.name}
          </Button>
        ))}
      </HStack>
    );
  }

  return (
    <Menu>
      <MenuButton
        as={Button}
        variant="ghost"
        size="md"
        leftIcon={showIcon ? <Icon as={MdLanguage} /> : undefined}
      >
        <HStack spacing={2}>
          <currentLanguage.Flag />
          <Text>{currentLanguage.name}</Text>
        </HStack>
      </MenuButton>
      <MenuList>
        {languages.map(lang => (
          <MenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            bg={baseLanguage === lang.code ? 'brand.50' : 'transparent'}
            fontWeight={baseLanguage === lang.code ? 'bold' : 'normal'}
          >
            <HStack spacing={3}>
              <lang.Flag />
              <Text>{lang.name}</Text>
            </HStack>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

export default LanguageSwitcher;
